import React, { useEffect, useState } from "react";
import useVocab from "../../hooks/useVocab";
import { Vocab } from "../../types/category";
import "./Practice.css";
import { isKana, toKana, toRomaji } from "wanakana";
import { useTranslation } from "react-i18next"; // 新增
import { getMeaning } from "../../functions/helper";

const Practice = () => {
	const { t } = useTranslation(); // 新增
	const params = new URLSearchParams(window.location.search);

	const chapterIds = params.get("chapters")?.split(",") || [];
	const { categories } = useVocab(chapterIds);
	const [total, setTotal] = useState(0);
	const [vocabList, setVocabList] = useState<Vocab[]>([]);
	const [wrongList, setWrongList] = useState<Vocab[]>([]);
	const [finish, setFinish] = useState(false);
	const [done, setDone] = useState(false);
	const [lastVocab, setLastVocab] = useState<(Vocab & { inputAnswer: string }) | null>(null);

	const [accuracy, setAccuracy] = useState<Record<string, number>>({});

	const [currentIndex, setCurrentIndex] = useState(0);
	const [userInput, setUserInput] = useState("");
	const [correctNumber, setCorrectNumber] = useState(0);
	const [feedback, setFeedback] = useState<null | "correct" | "wrong">(null);
	const [loading, setLoading] = useState(true);

	const [showHint, setShowHint] = useState(false);

	const contentRef = React.useRef<HTMLDivElement>(null);

	// Helper to shuffle array
	const shuffle = (arr: Vocab[]) => {
		const array = [...arr];
		for (let i = array.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[array[i], array[j]] = [array[j], array[i]];
		}
		return array;
	};

	useEffect(() => {
		if (!categories) return;
		console.log(categories);
		const vocabList = shuffle(categories.map((category) => category.chapters.map((chapter) => chapter.vocabulary).flat()).flat());
		console.log(vocabList);
		setTotal(vocabList.length);
		setVocabList(vocabList);
		setLoading(false);
	}, [categories]);

	const currentVocab = vocabList[currentIndex];

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!userInput) return;
		const isCorrect = currentVocab.answer.some((ans) => ans === toKana(userInput.trim()));
		let newWrongList = [...wrongList];
		if (isCorrect) {
			setCorrectNumber((prev) => prev + 1);
		} else {
			newWrongList.push(currentVocab);
			setWrongList(newWrongList);
		}

		setLastVocab({ ...currentVocab, inputAnswer: userInput });
		setAccuracy((prev) => ({
			...prev,
			[currentVocab.word]: prev[currentVocab.word] ? prev[currentVocab.word] + 1 : 1,
		}));
		setFeedback(isCorrect ? "correct" : "wrong");
		setUserInput("");
		if (currentIndex < vocabList.length - 1) {
			setCurrentIndex((idx) => idx + 1);
		} else {
			if (newWrongList.length === 0) {
				setDone(true);
				return;
			}
			setFinish(true);
			setCurrentIndex(0);
			setVocabList(shuffle(newWrongList));
			setWrongList([]);
		}
	};

	if (loading) {
		return <div className="practice-container glass-card">{t("practice.loading")}</div>;
	}
	if (vocabList.length === 0) {
		return <div className="practice-container glass-card">{t("practice.noVocab")}</div>;
	}
	if (done) {
		// 依章節分組 accuracy
		const chapterMap: Record<string, { chapterTitle: string; words: { word: string; count: number }[] }> = {};
		categories.forEach((cat) => {
			cat.chapters.forEach((chapter) => {
				const words = chapter.vocabulary
					.filter((vocab) => accuracy[vocab.word])
					.map((vocab) => ({
						word: vocab.word,
						count: accuracy[vocab.word],
					}));
				if (words.length > 0) {
					chapterMap[chapter.id] = {
						chapterTitle: chapter.title,
						words,
					};
				}
			});
		});

		return (
			<div className="practice-summary-container">
				<h1 className="practice-title">日本語の単語</h1>
				<p>{t("practice.doneDesc")}</p>
				<div className="practice-summary">
					{Object.values(chapterMap).map((chapter) => (
						<div
							key={chapter.chapterTitle}
							className="practice-summary-chapter">
							<div className="practice-summary-chapter-title">
								{chapter.chapterTitle} ({((chapter.words.filter(({ count }) => count === 1).length / chapter.words.length) * 100).toFixed(2)}
								%)
							</div>
							<div className="practice-summary-grid">
								{chapter.words.map(({ word, count }) => (
									<div
										key={word}
										className="practice-summary-item">
										<span className="practice-label">{word}</span> {count} {t("practice.times")}
									</div>
								))}
							</div>
						</div>
					))}
				</div>
				<div className="practice-actions">
					<button onClick={() => (window.location.href = "/")}>{t("practice.backHome")}</button>
					<button onClick={() => window.location.reload()}>{t("practice.retry")}</button>
				</div>
			</div>
		);
	}
	return (
		<div className="practice-container">
			<h1 className="practice-title">日本語の単語</h1>
			<div className="practice-progress">
				{finish && (
					<p
						style={{
							marginBottom: "0.5rem",
							marginTop: 0,
							fontSize: "1.5rem",
							color: "black",
						}}>
						{t("practice.retryDesc")}
					</p>
				)}
				{t("practice.progress")}： {correctNumber} / {total}
			</div>
			<div className="practice-word-box">
				<span className="practice-label">
					Q.
					{currentIndex + 1}
				</span>
				<span className="practice-word">
					{currentVocab.word}{" "}
					<i
						className="fa-solid fa-lightbulb"
						style={{
							color: "var(--primary-color)",
							cursor: "pointer",
							fontSize: "1.2rem",
						}}
						onClick={() => setShowHint(!showHint)}></i>
				</span>
				<div
					ref={contentRef}
					className={`practice-hint${showHint ? " open" : ""}`}
					style={{
						maxHeight: showHint ? (contentRef.current ? contentRef.current.scrollHeight : 999) : 0,
						overflow: "hidden",
					}}>
					{t("practice.meaning")}：{getMeaning(currentVocab)}
				</div>

				<div></div>
			</div>
			<div className="practice-form">
				<input
					type="text"
					className="practice-input"
					value={userInput}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							handleSubmit(e);
						}
					}}
					onChange={(e) => setUserInput(e.target.value)}
					autoFocus
				/>
			</div>

			{lastVocab && (
				<div
					className="practice-last "
					style={{ color: feedback === "correct" ? "green" : "red" }}>
					<p>
						<span className="practice-feedback-label">{t("practice.wordLabel")}</span> {lastVocab.word}
					</p>
					<p>
						<span className="practice-feedback-label">{t("practice.yourAnswer")}</span>
						{toKana(lastVocab.inputAnswer) + " (" + toRomaji(lastVocab.inputAnswer) + ")"}
					</p>
					<p>
						<span className="practice-feedback-label">{t("practice.answerLabel")}</span>{" "}
						{lastVocab.answer.map((ans) => ans + " (" + toRomaji(ans) + ")").join(", ")}
					</p>
					<p>
						<span className="practice-feedback-label">{t("practice.meaningLabel")}</span> {getMeaning(lastVocab)}
					</p>
				</div>
			)}
		</div>
	);
};

export default Practice;
